import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import {
  IGroupedPromotion,
  IMergedPromotions,
  convertPromotionMapToArray,
} from 'common/promotion';
import { DateFormat, IItinerary } from 'common/types';
import { days } from 'common/utils';
import { IPassengers, TripType } from 'contexts/searchParams';
import { IAirportDetail } from 'hooks/useAirportDetailList';
import { FilterBy, IFilters } from 'hooks/useDomesticFilters';
import { DepartureTime } from 'list/common/filter/DepartureTimeFilter';
import { IRange } from 'list/common/filter/PriceRange';
import { SortStyle } from 'list/common/sort/option';
import { getCardPromotions } from 'list/domestic/Booking/promotion';
import {
  IBookingParams,
  IDomesticFlights,
  IFare,
  IFlight,
  SeatClass,
} from './types';
import {
  DomesticItinerary,
  DomesticStatus,
  useDomesticFlightsOptions,
} from 'hooks/useDomesticFlights';
import { ISelectedFlight as IDomesticSelectedFlight } from 'list/domestic/selectedFlight';

dayjs.extend(isBetween);

const [DEPARTURE, ARRIVAL] = [0, 1];

const seatClassMap = new Map([
  [SeatClass.NON_REFUNDABLE, '특가석-환불불가'],
  [SeatClass.NO_FREE_BAGGAGE, '특가석-수하물유료'],
  [SeatClass.SPECIAL_DEAL, '특가석'],
  [SeatClass.DISCOUNT, '할인석'],
  [SeatClass.ECONOMY, '일반석'],
  [SeatClass.BUSINESS, '비즈니스석'],
]);

enum SortOrder {
  PRICE_ASC,
  PRICE_DESC,
  DEPARTURE_ASC,
  DEPARTURE_DESC,
  TIME_ASC,
  AIRLINE_ASC,
  AIRLINE_DESC,
}

const sortOptions = new Map([
  [
    SortOrder.PRICE_ASC,
    {
      title: '가격 낮은 순',
      sortBy: 'minFare',
      order: 1,
      style: SortStyle.ASCENDING,
      nclkCode: 'lsr.pricelow',
    },
  ],
  [
    SortOrder.PRICE_DESC,
    {
      title: '가격 높은 순',
      sortBy: 'minFare',
      order: -1,
      style: SortStyle.DESCENDING,
      nclkCode: 'lsr.pricehigh',
    },
  ],
  [
    SortOrder.DEPARTURE_ASC,
    {
      title: '출발시각 빠른 순',
      sortBy: 'departureTime',
      order: 1,
      style: SortStyle.ASCENDING,
      nclkCode: 'lsr.fasttime1',
    },
  ],
  [
    SortOrder.DEPARTURE_DESC,
    {
      title: '출발시각 늦은 순',
      sortBy: 'departureTime',
      order: -1,
      style: SortStyle.DESCENDING,
      nclkCode: 'lsr.lasttime',
    },
  ],
  [
    SortOrder.TIME_ASC,
    {
      title: '소요시간 짧은 순',
      sortBy: 'journeyTime',
      order: 1,
      style: SortStyle.ASCENDING,
      nclkCode: 'lsr.duration',
    },
  ],
]);

interface IPriceRange {
  min: number;
  max: number;
}

export interface IFilterOptions {
  airlines: Map<string, string>;
  prices: IPriceRange;
}

const getFilterOptions = (
  type: useDomesticFlightsOptions['type'],
  domesticStatus?: DomesticStatus,
): IFilterOptions => {
  const airlines = new Map<string, string>();
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  domesticStatus?.[type]?.airlines.forEach(airlineCode => {
    airlines.set(airlineCode, domesticStatus.airlinesCodeMap?.[airlineCode]);
  }) ?? [];

  return {
    airlines,
    prices: {
      min: domesticStatus?.[type]?.price.min || Number.MAX_SAFE_INTEGER,
      max: domesticStatus?.[type]?.price.max || Number.MIN_SAFE_INTEGER,
    },
  };
};

const extractFilterOptions = (
  result: IDomesticFlights,
  airlines: object = {},
  prices: IRange = {},
) =>
  Object.entries(result).reduce<IFilterOptions>(
    (aggs, [key, flights]) => {
      if (!['departures', 'arrivals'].includes(key)) return aggs;
      const { airlines: aggAirlines, prices: aggPrices } = aggs;
      flights.forEach(({ airlineCode, airlineName, minFare }) => {
        if (!aggAirlines.has(airlineCode)) {
          aggAirlines.set(airlineCode, airlineName);
        }
        aggPrices.min = Math.min(minFare, aggPrices.min);
        aggPrices.max = Math.max(minFare, aggPrices.max);
      });
      return aggs;
    },
    {
      airlines: new Map(Object.entries(airlines)),
      prices: {
        min: prices.min || Number.MAX_SAFE_INTEGER,
        max: prices.max || Number.MIN_SAFE_INTEGER,
      },
    },
  );

const getAdultTotalFare = ({ adultFare, aFuel, aTax, publishFee }: IFare) =>
  adultFare + aFuel + aTax + publishFee;

function getDepartureTimeGroup(departureTime: string) {
  if (departureTime < DepartureTime.T6) return DepartureTime.T0;
  if (departureTime < DepartureTime.T9) return DepartureTime.T6;
  if (departureTime < DepartureTime.T12) return DepartureTime.T9;
  if (departureTime < DepartureTime.T15) return DepartureTime.T12;
  if (departureTime < DepartureTime.T18) return DepartureTime.T15;
  if (departureTime < DepartureTime.T21) return DepartureTime.T18;
  return DepartureTime.T21;
}

const filter = (flights: IFlight[], filters: IFilters, index: number) =>
  flights.reduce<IFlight[]>((prev, flight) => {
    for (const [key, filterItem] of Object.entries(filters)) {
      const selected =
        key === FilterBy.DEPARTURE_TIME ? filterItem[index] : filterItem;
      const value =
        key === FilterBy.DEPARTURE_TIME
          ? getDepartureTimeGroup(flight[key])
          : flight[key];
      if (
        key === FilterBy.PRICE_RANGE &&
        selected &&
        (value < selected.min || value > selected.max)
      ) {
        return prev;
      }
      if (
        key !== FilterBy.PRICE_RANGE &&
        selected.size > 0 &&
        !selected.has(value)
      ) {
        return prev;
      }
    }
    prev.push(flight);
    return prev;
  }, []);

interface ISelectedFlight {
  airlineCode: string;
  fitName: string;
  departureDate: string;
  seatClass: string;
}

const isSameFlight =
  (selected: ISelectedFlight) =>
  ({ airlineCode, fitName, departureDate, detailedClass, fare }: IFlight) =>
    airlineCode === selected.airlineCode &&
    fitName === selected.fitName &&
    departureDate === selected.departureDate &&
    (detailedClass === selected.seatClass ||
      fare.some(({ bookingClass }) => bookingClass === selected.seatClass)); // Support old URLs

const findSelectedFlight =
  (flights: IDomesticFlights, selected: ISelectedFlight[]) =>
  (field: string, index: number) =>
    selected[index] && flights[field].find(isSameFlight(selected[index]));

function getCloseDates(dateString: string, min?: string, max?: string) {
  const input = days(dateString, DateFormat.YMD);
  if (!input.isValid()) return [];
  const now = days().startOf('day');
  const startDate = days.max([
    input.subtract(10, 'day'),
    min ? days(min, DateFormat.YMD) : now,
  ]);
  const endDate = days.min([
    input.add(10, 'day'),
    ...(max ? [days(max, DateFormat.YMD)] : []),
  ]);
  if (!(startDate?.isValid() && endDate?.isValid())) return [];
  const diff = endDate.diff(startDate, 'day');
  if (diff < 0) return [];
  return [...Array(diff + 1)].map((v, i) => {
    const date = startDate.add(i, 'day');
    return {
      label: date.format('M.D.dd'),
      value: date.format(DateFormat.YMD),
    };
  });
}

interface IFarePerPerson {
  adult: number;
  child: number;
}

function getFavoriteData(
  selectedFlightList: IDomesticSelectedFlight[],
  itinerary: IItinerary[],
  airportMap: Map<string, IAirportDetail>,
  fare: IFarePerPerson,
  fareType: string,
  person: IPassengers,
  serviceUrl: string,
  trip: string,
) {
  const itin = selectedFlightList.map(selectedFlight => {
    const {
      airlineCode,
      airlineName = selectedFlight.segment.airlineName,
      departureCityCode,
      departureDate,
      arrivalCityCode,
      arrivalDate,
      dayDiff,
      departureTime,
      arrivalTime,
      journeyTime,
    } = getDomesticFlightInfo(selectedFlight);

    const [depDt, arrDt] = [departureDate, arrivalDate].map(date =>
      days(date, 'YYYYMMDDHHmm').format(DateFormat.DotYMD),
    );
    return {
      airlineCd: airlineCode,
      airlineName,
      depAp: departureCityCode,
      depDt,
      depTm: departureTime,
      arrAp: arrivalCityCode,
      arrDt,
      arrTm: arrivalTime,
      dayCnt: dayDiff,
      jrnyTm: journeyTime,
      viaCnt: 0,
    };
  });
  const { airports, cities, sdates } = itinerary.reduce<{
    airports: string[];
    cities: string[];
    sdates: string[];
  }>(
    (prev, { departureAirport, arrivalAirport, departureDate }) => ({
      airports: prev.airports.concat(departureAirport, arrivalAirport),
      cities: prev.cities.concat(
        airportMap.get(departureAirport)?.cityName || '',
        airportMap.get(arrivalAirport)?.cityName || '',
      ),
      sdates: prev.sdates.concat(
        days(departureDate, 'YYYYMMDD').format(DateFormat.DotYMD),
      ),
    }),
    { airports: [], cities: [], sdates: [] },
  );
  const { adult: adultCnt, child: childCnt, infant: infantCnt } = person;
  return {
    flightData: {
      fare: fare.adult,
      fareTp: null,
      itin,
      seatSt: 'HK',
      serviceUrl,
    },
    groupParam: {
      adultCnt,
      childCnt,
      infantCnt,
      airports,
      cities,
      fareTp: fareType,
      sdates,
      ticketTp: 'rt',
      tripTp: trip,
    },
  };
}

function getSavedFilters(saved: any): IFilters | undefined {
  if (!saved) {
    return undefined;
  }

  const {
    seatClass = [],
    airlineCode = {},
    departureTime = [],
    minFare = 0,
  } = saved;
  return {
    [FilterBy.SEAT_CLASS]: new Set(seatClass),
    [FilterBy.AIRLINE]: new Set(Object.keys(airlineCode)),
    [FilterBy.DEPARTURE_TIME]: departureTime.map((el: string) => new Set(el)),
    [FilterBy.PRICE_RANGE]: minFare,
  };
}

export interface IFareAggs {
  otaId: string;
  bookingParams: IBookingParams[];
  cardPromotions: IMergedPromotions;
  discount?: IGroupedPromotion;
  fare: number;
  fareDetails: IFare[];
  farePerPerson: IFarePerPerson;
  supportNPay: boolean;
}

const getBookingParams = (
  domesticItinerary: DomesticItinerary,
  {
    adultFare,
    bookingClass,
    childFare,
    aFuel,
    cFuel,
    aTax,
    cTax,
    publishFee,
    etc,
  }: IFare,
) => ({
  airlineCode: domesticItinerary.segment.airlineCode,
  fitName: domesticItinerary.segment.flightNumber,
  depCity: domesticItinerary.segment.departure.airportCode,
  arrCity: domesticItinerary.segment.arrival.airportCode,
  departureDateTime:
    domesticItinerary.segment.departure.date +
    domesticItinerary.segment.departure.time,
  arrivalDateTime:
    domesticItinerary.segment.arrival.date +
    domesticItinerary.segment.arrival.time,
  seatClass: bookingClass,
  etc,
  seatCnt: domesticItinerary.seatCount,
  adultFare: adultFare + aTax + aFuel,
  childFare: childFare + cTax + cFuel,
  aFuel,
  cFuel,
  aTax,
  cTax,
  publishFee,
});

const getTotalFares = ({
  adultFare,
  childFare = 0,
  aFuel = 0,
  cFuel = 0,
  aTax = 0,
  cTax = 0,
  publishFee = 0,
}: IFare) => ({
  adult: adultFare + aFuel + aTax + publishFee,
  child: childFare + cFuel + cTax + publishFee,
});

function groupFaresByAgent(
  [departure, arrival]: DomesticItinerary[],
  passengers: IPassengers,
) {
  let agentMap = departure.fares.reduce((prev, fare) => {
    const departureFare = getTotalFares(fare);
    prev.set(fare.agtCode, {
      otaId: fare.agtCode,
      bookingParams: [getBookingParams(departure, fare)],
      cardPromotions: { descriptions: [], promotionMap: new Map() },
      fare:
        departureFare.adult * passengers.adult +
        departureFare.child * passengers.child,
      farePerPerson: departureFare,
      fareDetails: [fare],
      supportNPay: fare.supportNPay,
    });
    return prev;
  }, new Map<string, IFareAggs>());

  if (arrival) {
    agentMap = arrival.fares.reduce((prev, fare) => {
      const { agtCode, supportNPay } = fare;
      const prevAggs = agentMap.get(agtCode);
      if (prevAggs) {
        const arrivalFare = getTotalFares(fare);
        const { adult, child } = arrivalFare;
        prev.set(agtCode, {
          otaId: agtCode,
          bookingParams: prevAggs.bookingParams.concat(
            getBookingParams(arrival, fare),
          ),
          cardPromotions: { descriptions: [], promotionMap: new Map() },
          fare:
            prevAggs.fare +
            (arrivalFare.adult * passengers.adult +
              arrivalFare.child * passengers.child),
          farePerPerson: {
            adult: prevAggs.farePerPerson.adult + adult,
            child: prevAggs.farePerPerson.child + child,
          },
          fareDetails: prevAggs.fareDetails.concat(fare),
          supportNPay: prevAggs.supportNPay && supportNPay,
        });
      }
      return prev;
    }, new Map<string, IFareAggs>());
  }

  agentMap.forEach((aggs, otaId) => {
    const cardPromotions = getCardPromotions(aggs.fareDetails, passengers);
    agentMap.set(otaId, {
      ...aggs,
      cardPromotions,
      discount: convertPromotionMapToArray(
        cardPromotions.promotionMap,
        aggs.fare,
        otaId,
      ).sort((a, b) => a.discountedPrice - b.discountedPrice)[0],
    });
  });

  return agentMap;
}

function combineMinFares(
  passengers: IPassengers,
  selectedFlightList: DomesticItinerary[],
  trip: string,
) {
  if (trip !== TripType.RT) return [];
  const fares = selectedFlightList.map(flight => {
    const sorted = flight.fares
      .map(fare => {
        const fareDetails = [fare];
        const cardPromotions = getCardPromotions(fareDetails, passengers);
        const farePerPerson = getTotalFares(fare);
        const otaId = fare.agtCode;
        const totalFare =
          farePerPerson.adult * passengers.adult +
          farePerPerson.child * passengers.child;
        return {
          otaId,
          bookingParams: [getBookingParams(flight, fare)],
          cardPromotions,
          discount: convertPromotionMapToArray(
            cardPromotions.promotionMap,
            totalFare,
            otaId,
          ).sort((a, b) => a.discountedPrice - b.discountedPrice)[0],
          fare: totalFare,
          fareDetails,
          farePerPerson,
          supportNPay: fare.supportNPay,
        };
      })
      .sort((a, b) => {
        const [fareA, fareB] = [a, b].map(
          ({ discount, fare }) => discount?.discountedPrice || fare,
        );
        return fareA - fareB;
      });
    return sorted[0];
  });
  const [departure, arrival] = fares;
  return departure && arrival && departure.otaId !== arrival.otaId ? fares : [];
}

function hasSavedFilter(filters?: IFilters) {
  if (!filters) return false;
  const { seatClass, airlineCode, departureTime, minFare } = filters;
  return (
    seatClass.size > 0 ||
    airlineCode.size > 0 ||
    departureTime[DEPARTURE].size > 0 ||
    departureTime[ARRIVAL].size > 0 ||
    !!(minFare && (minFare.min || minFare.max))
  );
}

const parseSelectedFlight = (selectedFlight: string) =>
  selectedFlight
    ? selectedFlight?.split(',').map(flight => {
        const [airlineCode, fitName, departureDate, seatClass] =
          flight.split('-');
        return { airlineCode, fitName, departureDate, seatClass };
      })
    : undefined;

function sort(flights: IFlight[], sortOrder: SortOrder) {
  const found = sortOptions.get(sortOrder);

  if (!found) return flights;

  const { sortBy, order } = found;

  function compareFunction(a: IFlight, b: IFlight) {
    if (a[sortBy] === b[sortBy]) {
      if (a.departureTime === b.departureTime) return a.minFare - b.minFare;
      return a.departureTime < b.departureTime ? -1 : 1;
    }
    return a[sortBy] < b[sortBy] ? -order : order;
  }

  return flights.sort(compareFunction);
}

const stringifyRoutes = (routes: IItinerary[]) =>
  routes
    .map(
      ({ departureAirport, arrivalAirport, departureDate }) =>
        `${departureAirport}-${arrivalAirport}-${departureDate}`,
    )
    .join('/');

function stringifySelectedFlight(domesticItinerary?: DomesticItinerary) {
  if (!domesticItinerary) return '';

  return [
    domesticItinerary?.segment.airlineCode,
    domesticItinerary?.segment.flightNumber,
    domesticItinerary?.segment.departure.date +
      domesticItinerary?.segment.departure.time,
    domesticItinerary?.seatClass,
  ].join('-');
}

function stringifySelectedDomesticItinerary(itinerary?: DomesticItinerary) {
  if (!itinerary) return '';
  const {
    segment: {
      airlineCode,
      flightNumber: fitName,
      departure: { date, time },
    },
    seatClass: detailedClass,
  } = itinerary;
  return [airlineCode, fitName, date + time, detailedClass].join('-');
}

interface FlightInfo {
  codeshare?: string;
  airlineCode: string;
  airlineName?: string;
  flightNumber: string;
  departureCityCode: string;
  departureCityName?: string;
  departureDate: string;
  departureTime: string;
  arrivalCityCode: string;
  arrivalCityName?: string;
  arrivalDate: string;
  arrivalTime: string;
  dayDiff: number;
  journeyTime: string;
  detailedClass: string;
  minFare: number;
}

function getDomesticFlightInfo(itinerary: DomesticItinerary): FlightInfo;
function getDomesticFlightInfo(
  itinerary: DomesticItinerary,
  status: DomesticStatus,
  isDeparture?: boolean,
): Required<FlightInfo>;

function getDomesticFlightInfo(
  itinerary: DomesticItinerary,
  status?: DomesticStatus,
  isDeparture: boolean = true,
): Required<FlightInfo> | FlightInfo {
  const {
    duration,
    segment: { opCode, airlineCode, flightNumber, departure, arrival },
  } = itinerary ?? { segment: { departure: {}, arrival: {} } };

  if (!status) {
    return {
      airlineCode,
      flightNumber,
      departureCityCode: departure?.airportCode,
      departureDate: `${departure?.date}${departure?.time}`,
      departureTime: `${departure?.time?.slice(0, 2)}:${departure?.time?.slice(2, 4)}`,
      arrivalCityCode: arrival?.airportCode,
      arrivalDate: `${arrival?.date}${arrival?.time}`,
      arrivalTime: `${arrival?.time?.slice(0, 2)}:${arrival?.time?.slice(2, 4)}`,
      dayDiff: dayjs(`${departure?.date}${departure?.time}`, 'YYYYMMDD').diff(
        dayjs(`${arrival?.date}${arrival?.time}`, 'YYYYMMDD'),
        'day',
      ),
      journeyTime: `${Math.floor((duration ?? 0) / 60)
        .toString()
        .padStart(
          2,
          '0',
        )}:${((duration ?? 0) % 60).toString().padStart(2, '0')}`,
      detailedClass: itinerary.seatClass,
      minFare: itinerary.minFare,
    };
  }

  const { opCodeMap, airlinesCodeMap } = status ?? {};

  return {
    codeshare: opCodeMap?.[opCode],
    airlineCode,
    airlineName: airlinesCodeMap?.[airlineCode] ?? '',
    flightNumber,
    departureCityCode: departure?.airportCode,
    departureCityName:
      status?.[isDeparture ? 'departure' : 'arrival']?.depAirportName,
    departureDate: `${departure?.date}${departure?.time}`,
    departureTime: `${departure?.time?.slice(0, 2)}:${departure?.time?.slice(2, 4)}`,
    arrivalCityCode: arrival?.airportCode,
    arrivalCityName:
      status?.[isDeparture ? 'departure' : 'arrival']?.arrAirportName,
    arrivalDate: `${arrival?.date}${arrival?.time}`,
    arrivalTime: `${arrival?.time?.slice(0, 2)}:${arrival?.time?.slice(2, 4)}`,
    dayDiff: dayjs(`${departure?.date}${departure?.time}`, 'YYYYMMDD').diff(
      dayjs(`${arrival?.date}${arrival?.time}`, 'YYYYMMDD'),
      'day',
    ),
    journeyTime: `${Math.floor((duration ?? 0) / 60)
      .toString()
      .padStart(2, '0')}:${((duration ?? 0) % 60).toString().padStart(2, '0')}`,
    detailedClass: itinerary.seatClass,
    minFare: itinerary.minFare,
  };
}

export function getFlightArrivalTimeFilterByDate(
  dateString?: string,
  departureDates?: string[],
) {
  if (!dateString) {
    return undefined;
  }
  if (departureDates?.[0] !== departureDates?.[1]) {
    return null;
  }
  return `${dayjs(dateString, 'YYYYMMDDHHmm').format('HH:mm')}-23:59`;
}

export {
  SortOrder,
  ARRIVAL,
  DEPARTURE,
  seatClassMap,
  sortOptions,
  combineMinFares,
  getFilterOptions,
  extractFilterOptions,
  filter,
  findSelectedFlight,
  getAdultTotalFare,
  getCloseDates,
  getFavoriteData,
  getSavedFilters,
  groupFaresByAgent,
  hasSavedFilter,
  parseSelectedFlight,
  sort,
  stringifyRoutes,
  stringifySelectedFlight,
  stringifySelectedDomesticItinerary,
  getDomesticFlightInfo,
};
